import {
	type ActiveLineType,
	type ActiveOrderGetType,
	type ActiveOrderType,
	type ActivePurchaseType,
	type GetActivePurchase,
} from '../../../types/activePurchases.types'
import { OrderStatus, StatusLines } from '../../../types/purchases.types'

const MAX_DAYS = 10
const MILLISECONDS_PER_MINUTE = 1000
const SECONDS_PER_HOUR = 3600
const HOURS_PER_DAY = 24

const getStatus = ({
	status,
	lines,
}: {
	status: OrderStatus
	lines: ActiveLineType[]
}): OrderStatus | null => {
	const linesWithStock = lines.find(
		(line) => line.status === StatusLines.available
	)

	if (status in OrderStatus) {
		return linesWithStock ? status : OrderStatus.annulled
	}

	return null
}

const getOrderLines = ({ lines }: { lines: ActiveLineType[] }) =>
	lines.reduce((activeLines: ActiveLineType[], line) => {
		const index = activeLines.some(
			(activeLine) =>
				activeLine.id === line.id && activeLine.colorId === line.colorId
		)
		if (!index) {
			activeLines.push({
				status: line.status,
				id: line.id,
				colorId: line.colorId,
			})
		}
		return activeLines
	}, [])

const getActiveOrders = ({ orders }: { orders: ActiveOrderGetType[] }) => {
	const adaptedOrders: ActiveOrderType[] = []
	orders.forEach((order) => {
		const timeDifference =
			new Date().getTime() - new Date(order.creationDateTime).getTime()
		const daysDifference =
			timeDifference /
			(MILLISECONDS_PER_MINUTE * SECONDS_PER_HOUR * HOURS_PER_DAY)

		if (daysDifference < MAX_DAYS) {
			const adaptedOrder = {
				lines: getOrderLines({ lines: order.lines }),
				delivery: order.delivery,
				status: getStatus({ status: order.status, lines: order.lines }),
				deliveryMethod: order.deliveryMethod,
			}

			if (adaptedOrder.status && adaptedOrder.status in OrderStatus) {
				adaptedOrders.push(adaptedOrder as ActiveOrderType)
			}
		}
	})
	const nullStatusOrder = adaptedOrders.find((order) => !order.status)
	return nullStatusOrder ? null : adaptedOrders
}

const adaptActivePurchase = ({
	activePurchase,
}: {
	activePurchase: GetActivePurchase
}): ActivePurchaseType | null => {
	const adaptedActivePurchase = {
		purchaseId: activePurchase.purchaseId,
		orders: getActiveOrders({ orders: activePurchase.orders }),
		active: true,
		creationDateTime: activePurchase.creationDateTime,
		type: activePurchase.type,
	}
	if (!adaptedActivePurchase.orders?.length) {
		return null
	}
	return adaptedActivePurchase as ActivePurchaseType
}

export const adaptActivePurchases = ({
	activePurchases,
}: {
	activePurchases: GetActivePurchase[]
}): ActivePurchaseType[] => {
	const adaptedActivePurchases: ActivePurchaseType[] = []
	activePurchases?.forEach((activePurchase) => {
		const newPurchase = adaptActivePurchase({ activePurchase })
		if (newPurchase) {
			adaptedActivePurchases.push(newPurchase)
		}
	})

	return adaptedActivePurchases
}
